import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SMG_LOGO =
  'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png';

/**
 * Cinematic curtain-close/open page transition.
 * On route change: two red slabs sweep in from the edges to meet in the middle,
 * hold briefly (long enough for React Router to swap the tree),
 * then sweep back out revealing the new page.
 */
export default function PageTransition() {
  const location = useLocation();
  const [phase, setPhase] = useState('idle'); // 'idle' | 'cover' | 'uncover'
  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    // Curtain closes → hold → curtain opens (slower, more cinematic)
    setPhase('cover');
    const t1 = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      setPhase('uncover');
    }, 900);
    const t2 = setTimeout(() => setPhase('idle'), 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname]);

  const hidden = phase === 'idle';

  return (
    <div
      className={`fixed inset-0 z-[400] flex ${hidden ? 'pointer-events-none opacity-0' : 'pointer-events-none'}`}
      data-testid="page-transition"
      aria-hidden="true"
    >
      {/* Left slab */}
      <div
        className={`w-1/2 h-full origin-left transition-transform ease-[cubic-bezier(0.77,0,0.175,1)] duration-[750ms] ${
          phase === 'cover' ? 'scale-x-100' : 'scale-x-0'
        }`}
        style={{ background: '#0B0B10' }}
      />
      {/* Right slab */}
      <div
        className={`w-1/2 h-full origin-right transition-transform ease-[cubic-bezier(0.77,0,0.175,1)] duration-[750ms] ${
          phase === 'cover' ? 'scale-x-100' : 'scale-x-0'
        }`}
        style={{ background: '#0B0B10' }}
      />
      {/* Subtle radial glow behind logo */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
          phase === 'cover' ? 'opacity-100 delay-[550ms]' : 'opacity-0'
        }`}
      >
        <span
          className="absolute w-[60vw] h-[60vw] max-w-[720px] max-h-[720px] rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(closest-side, rgba(255,61,46,0.35), rgba(120,30,160,0.15) 50%, transparent 75%)' }}
        />
      </div>
      {/* Center logo while curtain is closed */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
          phase === 'cover' ? 'opacity-100 scale-100 delay-[550ms]' : 'opacity-0 scale-95'
        }`}
      >
        <img
          src={SMG_LOGO}
          alt="SMG"
          draggable={false}
          className="w-52 md:w-72 object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.7)]"
        />
      </div>
    </div>
  );
}
